import { customBaseQuery } from '@/lib/baseQuery';
import { RagGroupResponse } from '@/pages/mortgage-workflow/routes/Documents/types/document.type';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';

export const dealAPI = createApi({
  reducerPath: 'dealAPI',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    fetchCountryByIP: builder.query<
      { data: { country: string; ip: string } },
      void
    >({
      query: () => '/api/core/user-country/',
    }),

    getForexRate: builder.query<
      ApiResponse<any>,
      {
        fromCurrency: string;
        toCurrency: string;
        date?: Date | string;
        amount?: number;
      }
    >({
      query: ({ fromCurrency, toCurrency, date, amount = 1 }) => {
        const searchParams = new URLSearchParams({
          from_currency: fromCurrency,
          to_currency: toCurrency,
          amount: amount.toString(),
        });

        if (date) {
          const formattedDate =
            typeof date === 'string' ? date : date.toISOString().split('T')[0];
          searchParams.append('date', formattedDate);
        }

        return {
          url: `/forex/convert?${searchParams.toString()}`,
        };
      },
    }),
    getRagResponseFromDeal: builder.query<ApiResponse<any>, string>({
      query: (dealId) =>
        `/v1/crm-orchestration/get-all-rag-responses/deal/${dealId}`,
    }),
    getRagGroupResponseFromDeal: builder.query<
      ApiResponse<RagGroupResponse[]>,
      string
    >({
      query: (dealId) =>
        `/v1/crm-orchestration/deals/${dealId}/rag-responses/by-date`,
    }),
    categorizeDocumentsFromDeal: builder.mutation<
      ApiResponse<any>,
      { dealId: string; emails: string[]; destinationFolderId: string }
    >({
      query: ({ dealId, emails, destinationFolderId }) => ({
        url: `/v1/crm-orchestration/hubspot/deal/${dealId}/categorize-files`,
        method: 'POST',
        body: { emails, destinationFolderId },
      }),
    }),
    processMortgageDeal: builder.mutation<
      ApiResponse<any>,
      {
        dealId: string;
        emails: string[];
        destinationFolderId: string;
        options?: { applicationId?: string; dealType?: string };
      }
    >({
      query: ({ dealId, emails, destinationFolderId, options }) => ({
        url: `/v1/document-process/manual-deal`,
        method: 'POST',
        body: { dealId, emails, destinationFolderId, options },
      }),
    }),
  }),
});

export const {
  useGetForexRateQuery,
  useFetchCountryByIPQuery,
  useGetRagResponseFromDealQuery,
  useGetRagGroupResponseFromDealQuery,
  useCategorizeDocumentsFromDealMutation,
  useProcessMortgageDealMutation,
} = dealAPI;
